import React from "react";
import shortid from "shortid";
import styled from "styled-components";

import { getAlignmentsSafely } from "@examspark/api";
import { parseConnectionArray, parseObjectiveScope } from "@examspark/common";
import { FlexBox, colorFromTheme, glow } from "@examspark/common-ui";
import { DeliveryMediaType, Resource } from "@examspark/types";

import {
  getObjectiveAuthorityIdsFromAppState,
  useAppContext,
} from "../../../../providers/AppContext";

interface StyledProps {
  selected?: boolean;
}

const Styled = styled(FlexBox)<StyledProps>`
  padding: 0.8rem;
  flex-direction: row;
  border-bottom: solid 1px #ddd;
  align-items: center;
  transition: background-color 0.2s;
  border-left: solid 3px #fff;
  cursor: pointer;
  gap: 1rem;
  min-width: 0;

  &:hover {
    background-color: #f7f7f7;
    border-left: solid 3px #ccc;
  }

  outline-offset: -1px;
  &:focus-within {
    ${glow("outline", true)}
  }
  & input[type="checkbox"]:focus-visible {
    ${glow("outline")}
  }

  ${(props) => {
    if (props.selected) {
      return `
      border-left: solid 3px #006699 !important;
      `;
    }
  }}
`;

const ItemInput = styled.div`
  button {
    input {
      margin: 0;
    }
  }

  button:hover {
    background-color: #ddd;
  }
`;
const ItemMeta = styled.div`
  flex: 1;
  color: ${colorFromTheme("text")};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ObjectiveAbbreviations = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;
const ObjectiveAbbreviation = styled.span`
  font-family: input-mono;
  font-size: 0.9rem;
  color: #333;
  background-color: #f0f0f0;
  border: solid 1px #ddd;
  border-radius: 0.3rem;
  padding: 0.3rem;
`;
const PrintOnlyIndicator = styled.div`
  display: flex;
  align-items: center;
  svg {
    display: block;
    height: 12px;
    fill: #333;
  }
`;
const Passage = styled.span`
  font-size: 1rem;
  color: #333;
  background-color: #858ee0;
  border-radius: 0.3rem;
  padding: 0.3rem 0.5rem;
  white-space: nowrap;
  color: #fff;
`;

const CheckboxContainer = styled.label`
  border-radius: 0.5rem;
  padding: 0.5rem;
  cursor: pointer;
  display: inline-block;

  &:hover {
    background-color: #ddd;
  }

  input {
    cursor: pointer;
  }
`;

interface Props {
  item: Resource;
  onClick?: (item: Resource) => void;
  onToggleChecked?: (item: Resource) => void;
  selected?: boolean;
  checked?: boolean;
  disabled?: boolean;
}

const ActivityEditorItemPickerModalItem: React.FC<
  React.PropsWithChildren<Props>
> = (props) => {
  const [appState] = useAppContext();
  const preferredAuthorities = getObjectiveAuthorityIdsFromAppState(appState);
  const idRef = React.useRef<string>(shortid());

  return (
    <Styled
      selected={props.selected}
      id={props.item.id}
      onClick={() => props.onClick?.(props.item)}
    >
      <ItemInput>
        <CheckboxContainer>
          <input
            disabled={props.disabled}
            type="checkbox"
            checked={props.checked}
            onChange={(e) => {
              props.onToggleChecked?.(props.item);
            }}
            aria-describedby={idRef.current}
            // since the styled above isn't a proper button, we need a separate
            // path to activate preview without selecting the item
            // and this felt better than having 2 focusable, nested buttons
            onKeyUp={(e) => {
              if (e.key === " ") {
                props.onToggleChecked?.(props.item);
              } else if (e.key === "Enter") {
                props.onClick?.(props.item);
              }
            }}
          />
        </CheckboxContainer>
      </ItemInput>
      <ItemMeta id={idRef.current}>
        <div>{props.item.name}</div>
        <ObjectiveAbbreviations>
          {props.item.deliveryMediaType === DeliveryMediaType.PrintOnly && (
            <PrintOnlyIndicator title="Designed for Print Use">
              <svg
                id="Layer_1"
                enable-background="new 0 0 100 100"
                viewBox="0 0 100 100"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="m92.7099609 26.2799988h-9.4899902v-18.9899907c0-2.6099854-2.1298828-4.7600098-4.7600098-4.7600098h-56.9699688c-2.6099854 0-4.7600098 2.1199951-4.7600098 4.7600098v18.9899902h-9.4699706c-2.6099854 0-4.7600098 2.0700073-4.7600098 4.7600098v37.9799809c0 2.6000366 2.1300049 4.7600098 4.7600098 4.7600098h9.5v18.9299927c0 2.6100464 2.1199951 4.7600098 4.7600098 4.7600098h56.9599609c2.6101074 0 4.7600098-2.1199951 4.7600098-4.7600098v-18.9299927h9.5c2.6099854 0 4.7600098-2.1300049 4.7600098-4.7600098v-37.9799805c-.0500508-2.6599731-2.1799336-4.7600097-4.7900411-4.7600097zm-18.9899902 61.6700439h-47.4699688v-28.4300537h47.4699707v28.4300537zm0-61.6700439h-47.4699688v-14.2600098h47.4699707v14.2600098zm9.5 18.9899902c-2.6099854 0-4.7600098-2.1299438-4.7600098-4.7599487 0-2.6600342 2.130127-4.7700195 4.7600098-4.7700195 2.6101074 0 4.7600098 2.0800171 4.7600098 4.7700195-.0300293 2.6300049-2.1499024 4.7599487-4.7600098 4.7599487z" />
              </svg>
            </PrintOnlyIndicator>
          )}
          <ObjectiveAbbreviation>#{props.item.idInc}</ObjectiveAbbreviation>
          {getAlignmentsSafely(props.item.alignments).map((obj) => {
            const scope = parseObjectiveScope(obj.scope ?? "");
            if (
              scope &&
              preferredAuthorities?.length &&
              !preferredAuthorities.includes(scope.authority ?? "")
            ) {
              return null;
            }

            return (
              <ObjectiveAbbreviation key={obj.id}>
                {obj.abbreviation}
              </ObjectiveAbbreviation>
            );
          })}
        </ObjectiveAbbreviations>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
          {parseConnectionArray(props.item?.contexts)?.map((ctx) => {
            return <Passage key={ctx.id}>{ctx.name?.substring(0, 50)}</Passage>;
          })}
        </div>
      </ItemMeta>
    </Styled>
  );
};

export default ActivityEditorItemPickerModalItem;
